<template>
  <div v-if="user">
    <v-app-bar flat absolute>
      <v-app-bar-nav-icon @click="toggleNav"></v-app-bar-nav-icon>
      <v-toolbar-title
        v-if="isMobile"
        class="text-subtitle-1 font-weight-medium text-truncate app-page-title"
      >
        {{ pageTitle }}
      </v-toolbar-title>
      <v-spacer></v-spacer>

      <pending-units v-if="can('appbar.pending_units')" />

      <reminders-modal
        :user="user"
        v-if="can('appbar.reminders') && !isSalesAgent"
      />
      <price-check-modal v-if="can('appbar.price_check') && !isSalesAgent" />
      <!-- Desktop: full identity + action cluster -->
      <template v-if="!isMobile">
        <h3 class="blue--text text-truncate username-label">
          {{ user.username }}
        </h3>
        <h3 class="red--text ml-2 hidden-md-and-down">
          {{ user.branch.branch_name }}
        </h3>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              class="mr-2"
              v-bind="attrs"
              v-on="on"
              @click="openQuickActions"
            >
              <v-icon>mdi-lightning-bolt</v-icon>
            </v-btn>
          </template>
          <span>Quick Actions ({{ quickActionsShortcut }})</span>
        </v-tooltip>
        <v-btn icon class="mr-2" @click="toggleDarkMode">
          <v-icon>{{
            darkMode ? "mdi-white-balance-sunny" : "mdi-weather-night"
          }}</v-icon>
        </v-btn>
        <v-btn icon class="mr-2" @click="logout">
          <v-icon>mdi-logout-variant</v-icon>
        </v-btn>
      </template>

      <!-- Mobile: single overflow menu keeps the bar uncluttered -->
      <v-menu v-else offset-y left min-width="200">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold">{{
                user.username
              }}</v-list-item-title>
              <v-list-item-subtitle>{{
                user.branch.branch_name
              }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item @click="openQuickActions">
            <v-list-item-icon class="mr-3">
              <v-icon>mdi-lightning-bolt</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Quick Actions</v-list-item-title>
          </v-list-item>
          <v-list-item @click="toggleDarkMode">
            <v-list-item-icon class="mr-3">
              <v-icon>{{
                darkMode ? "mdi-white-balance-sunny" : "mdi-weather-night"
              }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{
              darkMode ? "Light mode" : "Dark mode"
            }}</v-list-item-title>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item @click="logout">
            <v-list-item-icon class="mr-3">
              <v-icon color="red">mdi-logout-variant</v-icon>
            </v-list-item-icon>
            <v-list-item-title class="red--text">Logout</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import RemindersModal from "../remindersModal.vue";
import PriceCheckModal from "../priceCheckModal.vue";
import PendingUnits from "../Unit/pendingUnits.vue";

export default {
  data() {
    return {};
  },
  components: {
    RemindersModal,
    PriceCheckModal,
    PendingUnits,
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      darkMode: "theme/darkMode",
      hasPermission: "permission/hasPermission",
      user_permissions: "permission/user_permissions",
    }),
    quickActionsShortcut() {
      const isMac =
        typeof navigator !== "undefined" &&
        /Mac|iPhone|iPad/.test(navigator.platform || "");
      return isMac ? "⌘ + S" : "Ctrl + S";
    },
    // Hat-based: the Sales Agent workspace key, not the legacy access_level 7.
    isSalesAgent() {
      return this.hasPermission("sales.agent_workspace");
    },
    // Shown on phones so the user always knows which page they're on.
    pageTitle() {
      return (this.$route && this.$route.name) || "JMB";
    },
  },
  created() {},
  methods: {
    ...mapActions({
      signout: "auth/signout",
      toggleMini: "navbar/toggleMini",
      toggleDrawer: "navbar/toggleDrawer",
      toggleDarkMode: "theme/toggleDarkMode",
    }),
    // On desktop the nav icon collapses the permanent drawer to mini; on mobile
    // the drawer is `temporary`, so the icon must open/close it instead.
    toggleNav() {
      if (this.isMobile) this.toggleDrawer();
      else this.toggleMini();
    },
    changeRoute(route) {
      this.$router.push(route);
    },
    // Hat-based permission gate; accounts without hats get no access.
    can(key) {
      return this.hasPermission(key);
    },
    openQuickActions() {
      this.$root.$emit("open-quick-actions");
    },
    logout() {
      this.signout().then(() => {
        location.reload();
      });
    },
  },
};
</script>

<style scoped>
/* Keep the username from pushing the action icons off-screen on phones. */
.username-label {
  max-width: 120px;
}
@media (max-width: 599.98px) {
  .username-label {
    max-width: 72px;
  }
  .app-page-title {
    max-width: 46vw;
  }
}
</style>
